import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface SystemSetting {
  key: string;
  value: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  label: string;
  description: string | null;
  category: string;
  updatedAt: string;
  updatedBy: string | null;
}

interface SystemSettingsResponse {
  settings: SystemSetting[];
}

interface UpdateSettingsInput {
  settings: Array<{
    key: string;
    value: string;
  }>;
}

// ============ Query Hooks ============

/**
 * Fetch all system settings
 */
export function useSystemSettings() {
  return useQuery({
    queryKey: ['system-settings'],
    queryFn: () => api.get<SystemSettingsResponse>('/admin/settings'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Get a specific setting value (helper)
 */
export function useSystemSetting(key: string) {
  const { data, ...rest } = useSystemSettings();
  const setting = data?.settings.find((s) => s.key === key);
  return { setting, ...rest };
}

// ============ Mutation Hooks ============

/**
 * Update system settings (bulk)
 */
export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsInput) =>
      api.patch<SystemSettingsResponse>('/admin/settings', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
  });
}

// ============ Helper Functions ============

/**
 * Parse a setting value based on its type
 */
export function parseSettingValue(setting: SystemSetting): string | number | boolean | unknown {
  switch (setting.type) {
    case 'number':
      return parseInt(setting.value, 10);
    case 'boolean':
      return setting.value === 'true';
    case 'json':
      try {
        return JSON.parse(setting.value);
      } catch {
        return setting.value;
      }
    default:
      return setting.value;
  }
}

/**
 * Get settings grouped by category
 */
export function groupSettingsByCategory(settings: SystemSetting[]): Record<string, SystemSetting[]> {
  return settings.reduce((groups, setting) => {
    const category = setting.category || 'general';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(setting);
    return groups;
  }, {} as Record<string, SystemSetting[]>);
}
